import { ReactElement, useMemo } from 'react'
import { ColorRepresentation } from 'three'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { a, useSpring } from '@react-spring/three'
import { ThreeEvent } from '@react-three/fiber'
import { CONNECTOR_BOX_HEIGHT } from './constants'
import {
  ConnectorKind,
  getConnectorBoxPositionWithOffset,
  getConnectorBoxRotation,
  getConnectorBoxScale,
  getConnectorHalfBoxHeight,
  UNIT_BOX_GEOMETRY_ARGS,
} from './utils'

interface Props {
  position: ImmutableVector3
  domain: Domain
  relativePosition: number
  connectorKind: ConnectorKind
  isTarget?: boolean
  color?: string | ColorRepresentation
  onClick?: (event: ThreeEvent<MouseEvent>) => void
  isActive?: boolean
  disableDepthTest?: boolean
  maxScale?: number
  height?: number
  visible?: boolean
}

const BoxMesh = ({
  position,
  domain,
  connectorKind,
  isTarget = false,
  color = 'green',
  onClick,
  isActive = false,
  disableDepthTest = false,
  maxScale = 1.7,
  height = CONNECTOR_BOX_HEIGHT,
  visible = true,
}: Props): ReactElement => {
  const halfHeight = useMemo(() => getConnectorHalfBoxHeight(height), [height])

  const meshPosition = useMemo(
    () => getConnectorBoxPositionWithOffset(position, isTarget, connectorKind, height),
    [connectorKind, height, isTarget, position],
  )

  const rotation = useMemo(() => getConnectorBoxRotation(domain), [domain])

  const baseScale = useMemo(
    () => getConnectorBoxScale(halfHeight, connectorKind),
    [halfHeight, connectorKind],
  )

  const { scale } = useSpring({
    to: async next => {
      while (isActive) {
        await next({ scale: 1 })
        await next({ scale: maxScale })
      }
      if (!isActive) {
        await next({ scale: 1 })
      }
    },
    from: { scale: 1 },
    reset: true,
    config: { duration: 800 },
  })

  const meshScale = scale.to(
    s => [s * baseScale[0], s * baseScale[1], s * baseScale[2]] as [number, number, number],
  )

  return (
    <a.mesh
      onClick={onClick}
      rotation={rotation}
      position={meshPosition.v}
      scale={meshScale}
      onPointerOver={() => {
        document.body.style.cursor = 'pointer'
      }}
      onPointerOut={() => {
        document.body.style.cursor = 'auto'
      }}
      visible={visible}
    >
      <boxGeometry attach="geometry" args={UNIT_BOX_GEOMETRY_ARGS} />
      <meshStandardMaterial
        color={color as ColorRepresentation}
        opacity={isActive ? 0.9 : 0.5}
        transparent={true}
        depthTest={!disableDepthTest}
      />
    </a.mesh>
  )
}

export default BoxMesh
